import { type ReactNode, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import QRCode from "qrcode";
import {
  type LucideIcon,
  Utensils,
  Plane,
  Backpack,
  Download,
  Printer,
  Eye,
  Ticket,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAuth } from "contexts/AuthContext";
import { useModule } from "contexts/ModuleContext";
import { usePageHeader } from "contexts/PageHeaderContext";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Label } from "ui/label";
import { Badge } from "ui/badge";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from "ui/card";
import { Switch } from "ui/switch";
import { Checkbox } from "ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "ui/dialog";

type Meal = "Breakfast" | "Lunch" | "Dinner";
type Venue = "Najaf" | "Karbala";
type Breakdown = [number, number, number, number];
type Serving = {
  id: string;
  section: "dine" | "airport" | "tour";
  meal: Meal;
  title: string;
  info: string;
  age: Breakdown;
  category: Breakdown;
  flight?: string;
};
type Voucher = {
  number: string;
  tourId: string;
  operator: string;
  flight: string;
  infant: boolean;
};

// Static classes preserve each meal and serving section's visual identity.
const mealStyles: Record<Meal, { border: string; badge: string }> = {
  Breakfast: {
    border: "border-l-[#009f98]",
    badge: "bg-[#eafffc] !text-[#009f98]",
  },
  Lunch: {
    border: "border-l-[#e78300]",
    badge: "bg-[#fff5e9] !text-[#e78300]",
  },
  Dinner: {
    border: "border-l-[#6050ff]",
    badge: "bg-[#ede9ff] !text-[#6050ff]",
  },
};
const sectionStyles: Record<
  Serving["section"],
  { card: string; accent: string; value: string; icon: string }
> = {
  dine: {
    card: "bg-[#f0faf7] border-green-200",
    accent: "text-[#22b85d]",
    value: "text-green-800",
    icon: "bg-green-100 text-[#22b85d]",
  },
  airport: {
    card: "bg-blue-50 border-blue-200",
    accent: "text-[#3973ff]",
    value: "text-[#304c99]",
    icon: "bg-blue-100 text-[#3973ff]",
  },
  tour: {
    card: "bg-[#f7f3ff] border-purple-200",
    accent: "text-[#a044ff]",
    value: "text-[#6b219e]",
    icon: "bg-purple-100 text-[#a044ff]",
  },
};

const mockDate = "2026-09-08";
const mealTypes: Meal[] = ["Breakfast", "Lunch", "Dinner"];
const records: Serving[] = [
  {
    id: "dine-b",
    section: "dine",
    meal: "Breakfast",
    title: "Dine-in",
    info: "",
    age: [27, 106, 557, 194],
    category: [27, 44, 88, 725],
  },
  {
    id: "dine-l",
    section: "dine",
    meal: "Lunch",
    title: "Dine-in",
    info: "",
    age: [27, 108, 566, 197],
    category: [27, 45, 90, 736],
  },
  {
    id: "dine-d",
    section: "dine",
    meal: "Dinner",
    title: "Dine-in",
    info: "",
    age: [26, 105, 552, 193],
    category: [26, 44, 88, 718],
  },
  {
    id: "air-1",
    section: "airport",
    meal: "Breakfast",
    title: "Flight EY568 Arrival",
    flight: "EY568",
    info: "06:50 · Airport Pickup",
    age: [1, 6, 30, 10],
    category: [1, 2, 5, 39],
  },
  {
    id: "air-2",
    section: "airport",
    meal: "Breakfast",
    title: "Flight EX506 Arrival",
    flight: "EX506",
    info: "05:40 · Airport Pickup",
    age: [2, 7, 37, 13],
    category: [2, 3, 6, 48],
  },
  {
    id: "air-3",
    section: "airport",
    meal: "Breakfast",
    title: "Flight QR382 Departure",
    flight: "QR382",
    info: "06:50 · Airport Drop",
    age: [1, 4, 18, 6],
    category: [1, 1, 3, 24],
  },
  {
    id: "air-4",
    section: "airport",
    meal: "Breakfast",
    title: "Flight QR101 Arrival",
    flight: "QR101",
    info: "08:15 · Airport Pickup",
    age: [1, 4, 21, 7],
    category: [1, 2, 3, 27],
  },
  {
    id: "air-5",
    section: "airport",
    meal: "Lunch",
    title: "Flight EY998 Arrival",
    flight: "EY998",
    info: "15:45 · Airport Pickup",
    age: [1, 4, 21, 8],
    category: [1, 2, 3, 28],
  },
  {
    id: "air-6",
    section: "airport",
    meal: "Lunch",
    title: "Flight EY610 Arrival",
    flight: "EY610",
    info: "11:10 · Airport Pickup",
    age: [1, 5, 26, 9],
    category: [1, 2, 4, 34],
  },
  {
    id: "air-7",
    section: "airport",
    meal: "Dinner",
    title: "Flight EK628 Arrival",
    flight: "EK628",
    info: "22:40 · Airport Pickup",
    age: [2, 6, 33, 12],
    category: [2, 3, 5, 43],
  },
  {
    id: "tour-1",
    section: "tour",
    meal: "Lunch",
    title: "Qabrastan Ziarat",
    info: "12:00 - 14:23 · Saifee Mahal - Main Gate",
    age: [1, 4, 18, 6],
    category: [1, 1, 3, 24],
  },
];
// A second venue has its own smaller mock roster. Every mock infant is in GEN.
const venueRecords: Record<Venue, Serving[]> = {
  Najaf: records,
  Karbala: records
    .filter(
      (row) =>
        row.section === "dine" || ["air-1", "air-5", "tour-1"].includes(row.id),
    )
    .map((row) => ({
      ...row,
      age: row.age.map((value) => Math.floor(value / 2)) as Breakdown,
      category: (() => {
        const total = row.age.reduce(
          (sum, value) => sum + Math.floor(value / 2),
          0,
        );
        const first = row.category
          .slice(0, 3)
          .map((value) => Math.floor(value / 2));
        return [
          ...first,
          total - first.reduce((sum, value) => sum + value, 0),
        ] as Breakdown;
      })(),
    })),
};
const operators = ["Faiz E Husaini Iraq Tours", "Al Safa Travel & Tours"];
const sum = (values: number[]) =>
  values.reduce((total, value) => total + value, 0);
const pax = (row: Serving, infants: boolean) =>
  sum(row.age) - (infants ? 0 : row.age[0]);
const xmlEscape = (value: string) =>
  value.replace(
    /[<>&"']/g,
    (character) =>
      ({
        "<": "&lt;",
        ">": "&gt;",
        "&": "&amp;",
        '"': "&quot;",
        "'": "&apos;",
      })[character] ?? character,
  );

function VoucherQR({ number }: { number: string }) {
  const qr = useMemo(
    () => QRCode.create(number, { errorCorrectionLevel: "M" }),
    [number],
  );
  const size = qr.modules.size;
  const path: string[] = [];
  for (let row = 0; row < size; row++)
    for (let column = 0; column < size; column++) {
      if (qr.modules.get(row, column))
        path.push(`M${column + 4} ${row + 4}h1v1h-1z`);
    }
  return (
    <svg
      role="img"
      aria-label={`QR code for ${number}`}
      className="h-[60px] w-[60px] shrink-0"
      viewBox={`0 0 ${size + 8} ${size + 8}`}
      shapeRendering="crispEdges"
    >
      <rect width="100%" height="100%" fill="white" />
      <path d={path.join("")} fill="black" />
    </svg>
  );
}
function VoucherTicket({ voucher }: { voucher: Voucher }) {
  return (
    <div className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-xl border border-dashed border-border border-l-4 border-l-[#009f98] bg-white p-3 text-foreground [border-left-style:solid] sm:px-4 print:mt-4 print:break-inside-avoid">
      <div className="min-w-0">
        <strong className="text-sm [overflow-wrap:anywhere]">
          {voucher.number}
        </strong>
        <p className="mt-1.5 text-xs [overflow-wrap:anywhere]">
          {voucher.tourId} · {voucher.operator} · {voucher.flight}
        </p>
      </div>
      <VoucherQR number={voucher.number} />
    </div>
  );
}
function ServingSection({
  kind,
  title,
  icon: Icon,
  details,
  filter,
  children,
}: {
  kind: Serving["section"];
  title: string;
  icon: LucideIcon;
  details?: ReactNode;
  filter?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card
      className={`!mt-0 min-w-0 !gap-0 print:shadow-none print:backdrop-blur-none ${sectionStyles[kind].card}`}
    >
      <CardHeader className="!flex flex-row flex-wrap items-center !gap-3 !p-4 sm:!p-5">
        <span
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${sectionStyles[kind].icon}`}
        >
          <Icon className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1">
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
          {details && (
            <CardDescription className="mt-1 text-sm">
              {details}
            </CardDescription>
          )}
        </div>
        {filter}
      </CardHeader>
      <CardContent className="min-w-0 !p-4 !pt-0 sm:!p-5 sm:!pt-0">
        {children}
      </CardContent>
    </Card>
  );
}

function MealFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        aria-label={label}
        className="w-full bg-background sm:ml-auto sm:w-40 print:hidden"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Meals</SelectItem>
        {mealTypes.map((meal) => (
          <SelectItem key={meal} value={meal}>
            {meal}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function MawaidDashboardPage() {
  const { setPageHeader } = usePageHeader();
  const { user, permissions } = useAuth();
  const { getScreenIdByPath } = useModule();
  const screenId = getScreenIdByPath("/mawaid-dashboard");
  const canGenerate =
    !!user?.isSuperAdmin ||
    (screenId !== undefined && !!permissions?.[screenId]?.add);
  const [date, setDate] = useState(mockDate);
  const [venue, setVenue] = useState<Venue>("Najaf");
  const [infants, setInfants] = useState(true);
  const [airportMeal, setAirportMeal] = useState("all");
  const [tourMeal, setTourMeal] = useState("all");
  const [generated, setGenerated] = useState<Record<string, Voucher[]>>({});
  const [active, setActive] = useState<Serving | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  useEffect(() => {
    setPageHeader({ title: "Mawaid Dashboard", icon: <Utensils /> });
    return () => setPageHeader(null);
  }, [setPageHeader]);
  const rows = date === mockDate ? venueRecords[venue] : [];
  const dine = rows.filter((row) => row.section === "dine");
  const airport = rows.filter(
    (row) =>
      row.section === "airport" &&
      (airportMeal === "all" || row.meal === airportMeal),
  );
  const tours = rows.filter(
    (row) =>
      row.section === "tour" && (tourMeal === "all" || row.meal === tourMeal),
  );
  const recordKey = (row: Serving) => `${date}:${venue}:${row.id}`;
  const visibleVouchers = (row: Serving) =>
    (generated[recordKey(row)] || []).filter(
      (voucher) => infants || !voucher.infant,
    );
  const vouchers = active ? visibleVouchers(active) : [];
  const groups = operators
    .map((operator) => ({
      operator,
      vouchers: vouchers.filter((voucher) => voucher.operator === operator),
    }))
    .filter((group) => group.vouchers.length);
  const generate = (row: Serving) => {
    const flight = row.flight;
    if (!canGenerate || !flight) return;
    const total = sum(row.age);
    const batch = row.id.replace("air-", "AIR");
    const entries = Array.from(
      { length: total },
      (_, index): Voucher => ({
        number: `SAI-${date.replace(/-/g, "")}-${venue === "Najaf" ? "NJF" : "KRB"}-${batch}-${String(index + 1).padStart(3, "0")}`,
        tourId: index < Math.ceil(total / 2) ? "TID-2600130" : "TID-2600131",
        operator: operators[index < Math.ceil(total / 2) ? 0 : 1],
        flight,
        infant: index < row.age[0],
      }),
    );
    setGenerated((current) =>
      current[recordKey(row)]
        ? current
        : { ...current, [recordKey(row)]: entries },
    );
  };
  const selectVouchers = (entries: Voucher[], checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      entries.forEach((voucher) =>
        checked ? next.add(voucher.number) : next.delete(voucher.number),
      );
      return next;
    });
  const exportExcel = () => {
    const data = [
      [
        "Date",
        "Venue",
        "Section",
        "Meal",
        "Flight / Tour",
        "Details",
        "Pax",
        "INF",
        "CHD",
        "ADL",
        "SNR",
        "VIP",
        "OFC",
        "EMP",
        "GEN",
      ],
      ...[...dine, ...airport, ...tours].map((row) => [
        date,
        venue,
        row.section,
        row.meal,
        row.title,
        row.info,
        String(pax(row, infants)),
        ...row.age.map(String),
        ...row.category.map((value, index) =>
          String(value - (!infants && index === 3 ? row.age[0] : 0)),
        ),
      ]),
    ];
    const xml = `<?xml version="1.0" encoding="UTF-8"?><?mso-application progid="Excel.Sheet"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"><Worksheet ss:Name="Mawaid Dashboard"><Table>${data.map((row) => `<Row>${row.map((value) => `<Cell><Data ss:Type="String">${xmlEscape(value)}</Data></Cell>`).join("")}</Row>`).join("")}</Table></Worksheet></Workbook>`;
    const url = URL.createObjectURL(
      new Blob([xml], { type: "application/vnd.ms-excel;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `mawaid-dashboard-${venue}-${date}.xml`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const renderCard = (row: Serving) => (
    <Card
      role="article"
      key={row.id}
      className={`!mt-0 min-w-0 !gap-0 border-border border-l-4 bg-white shadow-sm print:break-inside-avoid print:backdrop-blur-none ${mealStyles[row.meal].border}`}
      aria-label={row.section === "dine" ? `Dine-in ${row.meal}` : row.title}
    >
      <CardContent className="!p-4 sm:!p-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="break-words text-sm font-semibold">{row.title}</h3>
            {row.info && <p className="mt-1 break-words text-xs">{row.info}</p>}
          </div>
          <Badge
            className={`gap-2 !rounded-full border-0 px-3 py-1.5 !text-sm ${mealStyles[row.meal].badge}`}
          >
            <i className="h-2.5 w-2.5 rounded-full bg-current" />
            {row.meal}
          </Badge>
        </div>
        <div
          className="my-3 text-2xl font-semibold"
          aria-label={`${pax(row, infants)} Pax`}
        >
          {pax(row, infants).toLocaleString("en-US")}
        </div>
        <div className="mt-3 border-t border-border pt-3">
          <h4 className="mb-2 text-xs font-semibold">By Age</h4>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {["INF", "CHD", "ADL", "SNR"].map((label, index) => (
              <span
                key={label}
                className={`min-w-0 rounded-md bg-sky-50 px-1 py-1.5 text-center text-xs ${!infants && index === 0 ? "opacity-50 line-through" : ""}`}
                title={
                  !infants && index === 0
                    ? "Infants excluded from total count"
                    : undefined
                }
              >
                {label}: <b>{row.age[index]}</b>
              </span>
            ))}
          </div>
        </div>
        <div className="mt-3 border-t border-border pt-3">
          <h4 className="mb-2 text-xs font-semibold">By Category</h4>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {["VIP", "OFC", "EMP", "GEN"].map((label, index) => (
              <span
                key={label}
                className="min-w-0 rounded-md bg-sky-50 px-1 py-1.5 text-center text-xs"
              >
                {label}:{" "}
                <b>
                  {row.category[index] -
                    (!infants && index === 3 ? row.age[0] : 0)}
                </b>
              </span>
            ))}
          </div>
        </div>
        {row.section === "airport" && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4 print:hidden">
            {generated[recordKey(row)] ? (
              <>
                <Badge
                  variant="outline"
                  className="!whitespace-normal bg-background px-3 py-2 text-xs font-semibold"
                >
                  {visibleVouchers(row).length} Vouchers Generated
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-primary"
                  aria-label={`View vouchers for ${row.flight}`}
                  onClick={() => {
                    setActive(row);
                    setSelected(new Set());
                    setCollapsed(new Set());
                  }}
                >
                  <Eye />
                </Button>
              </>
            ) : (
              <Button
                className="text-white"
                disabled={!canGenerate}
                onClick={() => generate(row)}
                aria-label={`Generate voucher for ${row.flight}`}
              >
                <Ticket />
                Generate Voucher
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
  return (
    <section
      id="mawaid-dashboard"
      className="flex min-w-0 flex-col gap-6 pb-8 text-foreground print:absolute print:left-0 print:top-0 print:w-full print:p-0 print:[page:mawaidDashboard] print:[body:has(&)_*]:invisible print:!visible print:[&_*]:!visible print:[&_*]:[print-color-adjust:exact] print:[&_*]:[-webkit-print-color-adjust:exact] print:[body:has(#mawaid-voucher-print)_&]:hidden"
      aria-label="Mawaid Dashboard"
    >
      <style>{`@page mawaidDashboard { size: A4; margin: 12mm; }`}</style>
      <Card className="!mt-0 min-w-0 border-[#5B9BD5]/30 bg-white print:hidden">
        <CardContent className="flex flex-wrap items-end gap-4 !p-4 sm:!p-6">
          <div className="w-full min-w-0 space-y-2 sm:w-44">
            <Label htmlFor="mawaid-dashboard-date">
              Date <span className="text-destructive">*</span>
            </Label>
            <Input
              type="date"
              id="mawaid-dashboard-date"
              className="block h-9 min-w-0"
              aria-label="Dashboard Date"
              required
              value={date}
              onChange={(event) => {
                if (event.target.value) setDate(event.target.value);
              }}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <strong>Venue:</strong>
            {(["Najaf", "Karbala"] as Venue[]).map((value) => (
              <Button
                key={value}
                variant={value === venue ? "default" : "secondary"}
                className="min-w-20"
                aria-pressed={venue === value}
                onClick={() => setVenue(value)}
              >
                {value}
              </Button>
            ))}
          </div>
          <label className="flex items-center gap-3 self-center text-sm">
            <Switch checked={infants} onCheckedChange={setInfants} />
            Include infants in total count
          </label>
          <div className="flex shrink-0 items-center gap-3 sm:ml-auto">
            <Button variant="outline" onClick={exportExcel}>
              <Download />
              Excel
            </Button>
            <Button variant="outline" onClick={() => window.print()}>
              <Printer />
              Print
            </Button>
          </div>
        </CardContent>
      </Card>
      <div className="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-3">
        {[
          {
            title: "Dine-in Servings",
            icon: Utensils,
            type: "dine",
            data: dine,
          },
          {
            title: "Airport Coupons",
            icon: Plane,
            type: "airport",
            data: airport,
          },
          {
            title: "Atraaf Tour Packets",
            icon: Backpack,
            type: "tour",
            data: tours,
          },
        ].map(({ title, icon: Icon, type, data }) => (
          <Card
            key={type}
            className={`!mt-0 min-w-0 shadow-sm ${sectionStyles[type as Serving["section"]].card}`}
            aria-label={`${title} total`}
          >
            <CardContent className="flex items-center justify-between gap-3 !p-4 sm:!p-6">
              <div className="min-w-0">
                <p
                  className={`text-sm ${sectionStyles[type as Serving["section"]].accent}`}
                >
                  {title}
                </p>
                <strong
                  className={`text-2xl font-semibold ${sectionStyles[type as Serving["section"]].value}`}
                >
                  {sum(data.map((row) => pax(row, infants))).toLocaleString(
                    "en-US",
                  )}
                </strong>
              </div>
              <span
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${sectionStyles[type as Serving["section"]].icon}`}
              >
                <Icon className="h-6 w-6" />
              </span>
            </CardContent>
          </Card>
        ))}
      </div>
      <p className="hidden print:block">
        Mawaid Dashboard · {date} · {venue} · Infants{" "}
        {infants ? "included" : "excluded"}
      </p>
      <ServingSection kind="dine" title="Dine-in Servings" icon={Utensils}>
        <div className="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 print:!grid-cols-2">
          {dine.map(renderCard)}
        </div>
        {!dine.length && (
          <p className="py-5 text-sm text-muted-foreground">
            No dine-in servings for the selected date and venue.
          </p>
        )}
      </ServingSection>
      <ServingSection
        kind="airport"
        title="Airport Coupons"
        icon={Plane}
        details={
          <>
            {String(airport.length).padStart(2, "0")} Batches ·{" "}
            {sum(airport.map((row) => pax(row, infants)))} Pax
          </>
        }
        filter={
          <MealFilter
            label="Airport meal filter"
            value={airportMeal}
            onChange={setAirportMeal}
          />
        }
      >
        <div className="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 print:!grid-cols-2">
          {airport.map(renderCard)}
        </div>
        {!airport.length && (
          <p className="py-5 text-sm text-muted-foreground">
            No airport coupons match the selected filters.
          </p>
        )}
      </ServingSection>
      <ServingSection
        kind="tour"
        title="Atraaf Tour Packets"
        icon={Backpack}
        details={
          <>
            {String(tours.length).padStart(2, "0")} Batches ·{" "}
            {sum(tours.map((row) => pax(row, infants)))} Pax
          </>
        }
        filter={
          <MealFilter
            label="Tour meal filter"
            value={tourMeal}
            onChange={setTourMeal}
          />
        }
      >
        <div className="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 print:!grid-cols-2">
          {tours.map(renderCard)}
        </div>
        {!tours.length && (
          <p className="py-5 text-sm text-muted-foreground">
            No tour packets match the selected filters.
          </p>
        )}
      </ServingSection>
      <Dialog
        open={!!active}
        onOpenChange={(open) => {
          if (!open) setActive(null);
        }}
      >
        <DialogContent className="!flex max-h-[90vh] min-w-0 flex-col bg-white !p-4 sm:max-w-xl sm:!p-6">
          <DialogHeader>
            <DialogTitle className="break-words pr-6 text-primary">
              {active?.title}
            </DialogTitle>
            <DialogDescription>
              {active?.info} · {vouchers.length} Pax · {groups.length} Groups
            </DialogDescription>
          </DialogHeader>
          <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
            {groups.map((group) => {
              const count = group.vouchers.filter((voucher) =>
                selected.has(voucher.number),
              ).length;
              return (
                <Card
                  role="region"
                  className="!mt-0 min-w-0 !gap-0 overflow-hidden border-border bg-slate-50 shadow-none"
                  key={group.operator}
                  aria-label={group.operator}
                >
                  <CardHeader className="!flex flex-row items-center !gap-3 border-b !p-3 sm:!px-4">
                    <Checkbox
                      aria-label={`Select all vouchers for ${group.operator}`}
                      checked={
                        count === group.vouchers.length
                          ? true
                          : count > 0
                            ? "indeterminate"
                            : false
                      }
                      onCheckedChange={(checked) =>
                        selectVouchers(group.vouchers, checked === true)
                      }
                    />
                    <strong className="min-w-0 break-words text-sm">
                      {group.operator} · {group.vouchers.length} Pax
                    </strong>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="ml-auto shrink-0"
                      aria-label={`${collapsed.has(group.operator) ? "Expand" : "Collapse"} ${group.operator}`}
                      aria-expanded={!collapsed.has(group.operator)}
                      onClick={() =>
                        setCollapsed((current) => {
                          const next = new Set(current);
                          if (next.has(group.operator))
                            next.delete(group.operator);
                          else next.add(group.operator);
                          return next;
                        })
                      }
                    >
                      {collapsed.has(group.operator) ? (
                        <ChevronDown />
                      ) : (
                        <ChevronUp />
                      )}
                    </Button>
                  </CardHeader>
                  {!collapsed.has(group.operator) && (
                    <CardContent className="flex min-w-0 flex-col gap-3 !p-3 sm:!p-4">
                      {group.vouchers.map((voucher) => (
                        <div
                          className="flex min-w-0 items-center gap-2 sm:gap-4"
                          key={voucher.number}
                        >
                          <Checkbox
                            aria-label={`Select voucher ${voucher.number}`}
                            checked={selected.has(voucher.number)}
                            onCheckedChange={(checked) =>
                              selectVouchers([voucher], checked === true)
                            }
                          />
                          <VoucherTicket voucher={voucher} />
                        </div>
                      ))}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
          <DialogFooter className="shrink-0">
            <Button
              className="bg-[#fa6b6d] text-white hover:bg-[#ec6062]"
              onClick={() => setActive(null)}
            >
              Close
            </Button>
            <Button
              className="text-white"
              disabled={!selected.size}
              onClick={() => window.print()}
            >
              <Printer />
              Print Vouchers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {active &&
        createPortal(
          <div
            id="mawaid-voucher-print"
            className="hidden print:absolute print:left-0 print:top-0 print:block print:w-full print:!visible print:[page:mawaidDashboard] print:[&_*]:!visible print:[&_*]:[print-color-adjust:exact] print:[&_*]:[-webkit-print-color-adjust:exact]"
            data-testid="voucher-print-content"
            aria-hidden="true"
          >
            <h1>{active.title}</h1>
            <p>
              {date} · {venue} · {active.info} · {selected.size} Vouchers
            </p>
            {vouchers
              .filter((voucher) => selected.has(voucher.number))
              .map((voucher) => (
                <VoucherTicket key={voucher.number} voucher={voucher} />
              ))}
          </div>,
          document.body,
        )}
    </section>
  );
}
